import Management from "components/Management";
import AuthPage from "components/shared/AuthPage";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    fetchCheckAddingSales,
    fetchCloseTerm,
    fetchExportBeneficiariesFile,
    fetchExportFile,
    fetchExportStocks,
    fetchStats,
} from "store/managementStore";
import type { TPeriod } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";

interface ICloseTermParams {
    periodId: number;
    selectedRows: number[];
}

const ManagementPage = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const { activePeriod, allPeriods: periods } = useAppSelector((state) => state.period);

    const filteredPeriodId = state?.periodId;
    const filteredTPD = state?.tpd;

    const [currentPeriod, setCurrentPeriod] = useState<TPeriod | undefined>(undefined);
    const [termPeriodData, setTermPeriodData] = useState<number | undefined>(undefined);

    const {
        generalTabData,
        closeTermsStatus,
        stats,
        statsStatus,
        exportFileStatus,
        addSalesPendingStatus,
        addSalesCheckingStatus,
    } = useAppSelector((state) => state.management);

    const closeTerm = useCallback(
        (param: ICloseTermParams) => {
            dispatch(fetchCloseTerm(param));
        },
        [dispatch],
    );

    const addSale = useCallback(
        (titleId: number | null, termId: number) => {
            if (currentPeriod) {
                return dispatch(fetchCheckAddingSales({ titleId, periodId: currentPeriod.id, termId }));
            }
            return Promise.resolve(false);
        },
        [dispatch, currentPeriod],
    );

    const exportFile = useCallback(() => {
        if (currentPeriod) {
            dispatch(fetchExportFile(currentPeriod.id));
        }
    }, [dispatch, currentPeriod]);

    const exportBeneficiariesFile = useCallback(() => {
        if (currentPeriod) {
            dispatch(fetchExportBeneficiariesFile(currentPeriod.id));
        }
    }, [dispatch, currentPeriod]);

    const exportStockFile = useCallback(() => {
        if (currentPeriod) {
            dispatch(fetchExportStocks(currentPeriod.id));
        }
    }, [dispatch, currentPeriod]);

    const getStats = useCallback(
        (period: number) => {
            if (period) {
                dispatch(fetchStats(period));
            }
        },
        [dispatch],
    );

    useEffect(() => {
        if (activePeriod) {
            const period = periods.find((item) => item.id === (filteredPeriodId ?? activePeriod));

            if (period) {
                setCurrentPeriod(period);
                getStats(period.id);
            }

            if (filteredTPD) {
                setTermPeriodData(filteredTPD);
            }

            // Once filteredPeriodId is used, clear state so as not to be stuck on that ID.
            if (filteredPeriodId != null || filteredTPD != null) {
                navigate("/management", { replace: true });
            }
        }
    }, [activePeriod]);

    return (
        <AuthPage userAuthenticated pageName="management">
            <Management
                currentPeriod={currentPeriod}
                setCurrentPeriod={setCurrentPeriod}
                termPeriodData={termPeriodData}
                periods={periods}
                generalTabData={generalTabData}
                stats={stats}
                statsStatus={statsStatus}
                exportFileStatus={exportFileStatus}
                addSalesPendingStatus={addSalesPendingStatus}
                addSalesCheckingStatus={addSalesCheckingStatus}
                closeTermsStatus={closeTermsStatus}
                getStats={getStats}
                closeTerm={closeTerm}
                addSale={addSale}
                exportFile={exportFile}
                exportBeneficiariesFile={exportBeneficiariesFile}
                exportStockFile={exportStockFile}
            />
        </AuthPage>
    );
};

export default ManagementPage;
